import { useState } from 'react';
import { Space, Typography } from 'antd';

const KIND_COLORS = {
    shoot: '#12b76a',
    cc: '#f79009',
    reshoot: '#f04438',
};

function bucketLabel(iso, timeZone) {
    return new Date(iso).toLocaleTimeString('en-GB', {
        timeZone,
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12',
    });
}

function stackTotal(counts) {
    return Object.values(counts || {}).reduce((sum, count) => sum + count, 0);
}

export function consistencyColor(score) {
    if (score == null) return '#98a2b3';
    if (score < 20) return '#d92d20';
    if (score < 40) return '#f79009';
    if (score < 60) return '#eaaa08';
    if (score < 80) return '#12b76a';
    return '#027a48';
}

export function ConsistencyDial({ score }) {
    const size = 44;
    const stroke = 4;
    const radius = (size - stroke) / 2;
    const circumference = 2 * Math.PI * radius;
    const value = score == null ? 0 : Math.max(0, Math.min(100, score));
    const color = consistencyColor(score);
    return (
        <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            role="img"
            aria-label={score == null ? 'No consistency score' : `Consistency ${score}`}
        >
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#eaecf0" strokeWidth={stroke} />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth={stroke}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference - (value / 100) * circumference}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central" fontSize="11" fontWeight="600" fill={color}>
                {score == null ? '—' : score}
            </text>
        </svg>
    );
}

const DEFAULT_KINDS = [
    { key: 'shoot', label: 'Shoot', color: KIND_COLORS.shoot },
    { key: 'cc', label: 'CC pending', color: KIND_COLORS.cc },
    { key: 'reshoot', label: 'Reshoot', color: KIND_COLORS.reshoot },
];

export default function BayHistogram({
    histogram,
    kinds = DEFAULT_KINDS,
    emptyText = 'No designated hours on this bay',
    timeZone = 'Asia/Kolkata',
    zoneLabel = 'IST',
}) {
    const [hover, setHover] = useState(null);
    const slots = histogram || [];
    if (!slots.length) return <Typography.Text type="secondary">{emptyText}</Typography.Text>;
    const types = kinds.filter(kind => slots.some(slot => slot.counts?.[kind.key]));
    const max = Math.max(1, ...slots.map(slot => stackTotal(slot.counts)));
    const width = Math.max(480, slots.length * 28);
    const height = 160;
    const pad = { left: 28, right: 8, top: 8, bottom: 28 };
    const innerWidth = width - pad.left - pad.right;
    const innerHeight = height - pad.top - pad.bottom;
    const slotWidth = innerWidth / slots.length;
    const barWidth = Math.max(4, slotWidth * 0.7);
    const labelEvery = slots.length > 16 ? 4 : 2;
    const hovered = hover != null ? slots[hover] : null;

    return (
        <div>
            <Space size={12} wrap style={{ marginBottom: 8 }}>
                {types.map(kind => (
                    <span key={kind.key} style={{ fontSize: 12 }}>
                        <span style={{ display: 'inline-block', width: 8, height: 8, marginRight: 4, background: kind.color }} />
                        {kind.label}
                    </span>
                ))}
                {!types.length ? <Typography.Text type="secondary">No completions in these hours</Typography.Text> : null}
            </Space>
            <div style={{ position: 'relative', overflowX: 'auto' }} onMouseLeave={() => setHover(null)}>
                <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label="Bay completion histogram">
                    {slots.map((slot, index) => {
                        const x = pad.left + slotWidth * index + (slotWidth - barWidth) / 2;
                        let y = pad.top + innerHeight;
                        const showLabel = index % labelEvery === 0 || index === slots.length - 1;
                        return (
                            <g key={slot.start} onMouseEnter={() => setHover(index)}>
                                <rect x={pad.left + slotWidth * index} y={pad.top} width={slotWidth} height={innerHeight} fill="transparent" />
                                {types.map(kind => {
                                    const value = slot.counts?.[kind.key] || 0;
                                    const barHeight = (value / max) * innerHeight;
                                    y -= barHeight;
                                    if (!barHeight) return null;
                                    return <rect key={kind.key} x={x} y={y} width={barWidth} height={barHeight} fill={kind.color} />;
                                })}
                                {showLabel ? (
                                    <text x={pad.left + slotWidth * index + slotWidth / 2} y={height - 6} textAnchor="middle" fontSize="10" fill="#667085">
                                        {bucketLabel(slot.start, timeZone)}
                                    </text>
                                ) : null}
                            </g>
                        );
                    })}
                </svg>
                {hovered ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: 8,
                            top: 8,
                            background: '#fff',
                            border: '1px solid #e4e7ec',
                            borderRadius: 4,
                            padding: '6px 8px',
                            fontSize: 12,
                        }}
                    >
                        <div>{bucketLabel(hovered.start, timeZone)} {zoneLabel}</div>
                        {types.filter(kind => hovered.counts?.[kind.key]).map(kind => (
                            <div key={kind.key}>
                                {kind.label}: {hovered.counts[kind.key]}
                            </div>
                        ))}
                        {!stackTotal(hovered.counts) ? <div>No completions</div> : null}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
